import { Alert, VFlow } from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import { FonteIdadeGestacionalEnum, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import { toDate } from 'util/date/formatDate'

import { AcompanhamentoVulnerabilidadeSection } from '../../soap/aside/acompanhamento-vulnerabilidade/AcompanhamentoVulnerabilidadeSection'
import { UltimosContatosCard } from '../components/UltimosContatosCard'
import { ExternalPaths } from '../model-acompanhamentos'
import { EncaminhamentosGestacaoCard } from '../pre-natal/components/EncaminhamentosGestacaoCard'
import { ListaProblemasCondicoesCard } from '../pre-natal/components/ListaProblemasCondicoesCard'
import { MedicamentosEmUsoCard } from '../pre-natal/components/MedicamentosEmUsoCard'
import { ProximosAgendamentosCard } from '../pre-natal/components/ProximosAgendamentosCard'
import { VacinacaoGestacaoCard } from '../pre-natal/components/VacinacaoGestacaoCard'
import { ExamesPuerperioCard } from './components/ExamesPuerperioCard'
import { InformacoesPuerperioCard } from './components/InformacoesPuerperioCard'
import { MedicoesPuerperioCard } from './components/MedicoesPuerperioCard'
import { useAcompanhamentoPuerperio } from './hooks/useAcompanhamentoPuerperio'

interface AcompanhamentoPuerperioContentProps {
  prontuarioId: ID
  cidadaoId: ID
  atendimentoId: ID
  dataReferencia: Instant
  externalPaths: ExternalPaths
  isVisualizacaoProntuario: boolean
  isEscutaInicial: boolean
}

// TODO @RoyalFlush - Adicionar Cards do Puerpério.
export function AcompanhamentoPuerperioContent(props: Readonly<AcompanhamentoPuerperioContentProps>) {
  const {
    prontuarioId,
    cidadaoId,
    atendimentoId,
    dataReferencia,
    externalPaths,
    isVisualizacaoProntuario,
    isEscutaInicial,
  } = props

  const { PILOTO_VINCULACAO_CUIDADO_ENABLED } = useFlags()

  const { data: puerperio } = useAcompanhamentoPuerperio(prontuarioId, dataReferencia)
  const dataInicioPuerperio = puerperio?.duracaoPuerperio.startDate
  const dataFimPuerperio = puerperio?.duracaoPuerperio.endDate
  const dataInicioGestacao = toDate(puerperio?.dataInicioGestacao)
  const isFonteIgDum = puerperio?.fonteIdadeGestacional === FonteIdadeGestacionalEnum.DUM

  return (
    <VFlow>
      {isUndefinedOrNull(puerperio?.dataDesfechoGestacao) && (
        <Alert type='warning'>
          Esta gestação ainda não teve desfecho no sistema. O módulo foi habilitado automaticamente a partir da DPP (
          {isFonteIgDum ? 'DUM' : 'eco. obst.'}).
        </Alert>
      )}
      {!isVisualizacaoProntuario && !isEscutaInicial && (
        <Alert type='info'>Os dados inseridos no atendimento atual só serão exibidos no próximo atendimento.</Alert>
      )}
      {puerperio?.acompanhamentoAtivo && !puerperio?.puerperioAtivo && (
        <Alert type='info'>
          O módulo será exibido até 90 dias após o desfecho da gestação para auxiliar no acompanhamento do puerpério.
        </Alert>
      )}
      <InformacoesPuerperioCard
        prontuarioId={prontuarioId}
        cidadaoId={cidadaoId}
        atendimentoId={atendimentoId}
        dataInicioPuerperio={dataInicioPuerperio}
        dataFimPuerperio={dataFimPuerperio}
      />
      <MedicoesPuerperioCard prontuarioId={prontuarioId} dataInicioGestacao={dataInicioGestacao} />
      <ListaProblemasCondicoesCard
        prontuarioId={prontuarioId}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
        dataInicioGestacao={dataInicioGestacao}
        dataInicioPuerperio={dataInicioPuerperio}
        dataFimPuerperio={dataFimPuerperio}
      />
      <MedicamentosEmUsoCard
        prontuarioId={prontuarioId}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
        dataFimPuerperio={dataFimPuerperio}
      />
      <UltimosContatosCard
        cidadaoId={cidadaoId}
        atendimentoId={atendimentoId}
        dataInicioCondicao={dataInicioPuerperio}
        dataFimCondicao={dataFimPuerperio}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
        historicoPath={externalPaths.historicoPath}
        isEscutaInicial={isEscutaInicial}
      />
      <AcompanhamentoVulnerabilidadeSection
        cidadaoId={cidadaoId}
        dataReferencia={dataReferencia}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
      />
      <VacinacaoGestacaoCard
        dataInicioGestacao={toDate(puerperio?.dataInicioGestacao)}
        vacinacaoPath={externalPaths.vacinacaoPath}
        prontuarioId={prontuarioId}
        isEscutaInicial={isEscutaInicial}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
        dataInicioPuerperio={dataInicioPuerperio}
      />
      <ExamesPuerperioCard prontuarioId={prontuarioId} dataInicioPuerperio={dataInicioPuerperio} />
      {PILOTO_VINCULACAO_CUIDADO_ENABLED && (
        <EncaminhamentosGestacaoCard
          prontuarioId={prontuarioId}
          tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
          dataInicioCondicao={dataInicioGestacao ?? toDate(dataInicioPuerperio)}
          dataFimPuerperio={dataFimPuerperio}
        />
      )}
      <ProximosAgendamentosCard
        cidadaoId={cidadaoId}
        tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
        agendamentosPath={externalPaths.agendamentosPath}
      />
    </VFlow>
  )
}
